import * as O from 'fp-ts/lib/Option';
import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import {
  Height,
  isValidHeight,
  ValidHeight,
  isEmptyHeight,
  UnvalidatedHeight,
  EmptyHeight,
} from 'ApplicationEntry/models/Height';
import {Sex} from 'ApplicationEntry/models/Sex';
import {
  EmptySocialSecurityNumber,
  isValidSocialSecurityNumber,
  SocialSecurityNumber,
  ValidSocialSecurityNumber,
  UnvalidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {isValidZipCode, ValidZipCode, ZipCode, UnvalidatedZipCode, EmptyZipCode} from 'ApplicationEntry/models/ZipCode';
import {Email, isValidEmail, ValidEmail, EmptyEmail} from 'ApplicationEntry/models/Email';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {safeParseInt} from 'Shared/utilities/parsers';
import {flow} from 'fp-ts/lib/function';
import {PhoneNumber, isValidPhoneNumber, ValidPhoneNumber, EmptyPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {isValidDate} from 'ApplicationEntry/models/Date';
import {OccupationError, UserAssignedOccupation, Occupation} from './Occupation';
import {Async} from 'Core/models/Async';

export class InvalidOrdinaryApplicantDemographics {
  public readonly key = 'InvalidOrdinaryApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly hireDate: Date | '',
    public readonly email: Email,
    public readonly height: Height,
    public readonly weight: number | '',
    public readonly isFullTimeStudent: boolean | '',
    public readonly occupation: Occupation,
    public readonly phone: PhoneNumber,
    public readonly occSmokerCode: StringUnion
  ) {}
}

type AppDemographicsProp = {
  name: StringUnion;
  type: ApplicantType | '';
  sex: Sex | '';
  dateOfBirth: Date | '';
  address: StringUnion;
  addressLine2: StringUnion;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  socialSecurityNumber: SocialSecurityNumber;
  hireDate: Date | '';
  email: Email;
  height: Height;
  weight: number | '';
  isFullTimeStudent: boolean | '';
  occupation: Occupation;
  phone: PhoneNumber;
  occSmokerCode: StringUnion;
};

export class ValidOrdinaryApplicantDemographics {
  public static createFromApp({
    name,
    type,
    sex,
    dateOfBirth,
    address,
    addressLine2,
    city,
    state,
    zip,
    socialSecurityNumber,
    hireDate,
    email,
    height,
    weight,
    isFullTimeStudent,
    occupation,
    phone,
    occSmokerCode,
  }: AppDemographicsProp) {
    return this.create(
      name,
      type,
      sex,
      dateOfBirth,
      address,
      addressLine2,
      city,
      state,
      zip,
      socialSecurityNumber,
      hireDate,
      email,
      height,
      weight,
      isFullTimeStudent,
      occupation,
      phone,
      occSmokerCode
    );
  }

  public static create(
    name: StringUnion,
    type: ApplicantType | '',
    sex: Sex | '',
    dateOfBirth: Date | '',
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    socialSecurityNumber: SocialSecurityNumber,
    hireDate: Date | '',
    email: Email,
    height: Height,
    weight: number | '',
    isFullTimeStudent: boolean | '',
    occupation: Occupation,
    phone: PhoneNumber,
    occSmokerCode: StringUnion
  ) {
    return isValidSocialSecurityNumber(socialSecurityNumber) &&
      (isValidHeight(height) || isEmptyHeight(height)) &&
      isValidEmail(email) &&
      isValidZipCode(zip) &&
      isValidPhoneNumber(phone) &&
      isValidDate(dateOfBirth) &&
      isValidDate(hireDate)
      ? new ValidOrdinaryApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          hireDate,
          email,
          height,
          weight,
          isFullTimeStudent,
          occupation,
          phone,
          occSmokerCode
        )
      : new InvalidOrdinaryApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          hireDate,
          email,
          height,
          weight,
          isFullTimeStudent,
          occupation,
          phone,
          occSmokerCode
        );
  }
  public readonly key = 'ValidOrdinaryApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode | UnvalidatedZipCode,
    public readonly socialSecurityNumber: ValidSocialSecurityNumber | UnvalidSocialSecurityNumber,
    public readonly hireDate: Date | '',
    public readonly email: ValidEmail,
    public readonly height: ValidHeight | UnvalidatedHeight,
    public readonly weight: number | '',
    public readonly isFullTimeStudent: boolean | '',
    public readonly occupation: Occupation,
    public readonly phone: ValidPhoneNumber,
    public readonly occSmokerCode: StringUnion
  ) {}
}

export type OrdinaryApplicantDemographics = ValidOrdinaryApplicantDemographics | InvalidOrdinaryApplicantDemographics;

export type OrdinaryApplicantDemographicsOptions<TReturn> = ToMatchOptions<
  OrdinaryApplicantDemographics,
  'key',
  TReturn
>;

export const matchOrdinaryApplicantDemographics = <TReturn>(
  applicantDemographics: OrdinaryApplicantDemographics,
  options: OrdinaryApplicantDemographicsOptions<TReturn>
): TReturn => {
  switch (applicantDemographics.key) {
    case 'InvalidOrdinaryApplicantDemographics':
      return options.InvalidOrdinaryApplicantDemographics(applicantDemographics);
    case 'ValidOrdinaryApplicantDemographics':
      return options.ValidOrdinaryApplicantDemographics(applicantDemographics);
  }
};

export const EmptyOrdinaryApplicantDemographics: OrdinaryApplicantDemographics = new ValidOrdinaryApplicantDemographics(
  new EmptyString(),
  'Ind',
  '',
  '',
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  EmptySocialSecurityNumber,
  '',
  EmptyEmail,
  EmptyHeight,
  '',
  false,
  new UserAssignedOccupation('', '', '', ''),
  EmptyPhoneNumber,
  new EmptyString()
);

export const changeName = (name: string) => (existingApp: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...existingApp, name: NonEmptyString.create(name)});

export const changeType = (type: ApplicantType) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({
    ...app,
    type,
  });

export const changeSex = (sex: Sex) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, sex});

export const changeAddress = (address: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, address: NonEmptyString.create(address)});

export const changeAddressLine2 = (addressLine2: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, addressLine2: NonEmptyString.create(addressLine2)});

export const changeCity = (city: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, city: NonEmptyString.create(city)});

export const changeState = (state: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, zip});

export const changeDateOfBirth = (dateOfBirth: Date | '') => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, dateOfBirth});

export const changeSocialSecurityNumber = (socialSecurityNumber: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });

export const changeHireDate = (hireDate: Date | '') => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, hireDate});

export const changeEmail = (emailAddress: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, email: ValidEmail.create(emailAddress)});

export const changeHeight = (feet: number | '', inches: number | '') => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, height: ValidHeight.create(feet, inches)});

export const changeWeight = (weight: string) => (app: OrdinaryApplicantDemographics) => {
  const parseWeight = flow(
    safeParseInt,
    O.getOrElse<number | ''>(() => '')
  );

  return ValidOrdinaryApplicantDemographics.createFromApp({...app, weight: parseWeight(weight)});
};

export const changeIsFullTimeStudent = (isFullTimeStudent: boolean | '') => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, isFullTimeStudent});

export const changeOccupation = (occupation: Async<string>) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, occupation});

export const changeUserOccupation = (
  occupation: string,
  code: string,
  description: string,
  underwritingCode: string
) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({
    ...app,
    occupation: UserAssignedOccupation.create(occupation, code, description, underwritingCode),
  });

export const changeOccupationError = (occupation: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, occupation: OccupationError.create(occupation)});

export const changePhone = (phone: string) => (app: OrdinaryApplicantDemographics) =>
  ValidOrdinaryApplicantDemographics.createFromApp({...app, phone: ValidPhoneNumber.create(phone)});
